import React, { useState } from 'react';

import { Alert, Button, Empty, Select, Table, Tabs } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { T } from 'react-translator-component';

import { priorityColor, statusColor } from './statusLabels';
import { Badge, Header, StatCard, StatsRow, TicketCell, Wrapper } from './styled';
import { useTickets } from './useTickets';

import { Pagination } from '../../molecules/Pagination/Pagination';
import NewTicketModal from '../../organisms/Tickets/NewTicketModal';
import TicketDrawer from '../../organisms/Tickets/TicketDrawer';

import { TicketSummary } from '../../../types/responses/Jira';

const FALLBACK_COLOR = '#8c8c8c';

const TicketsPage = () => {
  const s = useTickets();
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  if (s.forbidden) {
    return (
      <Wrapper>
        <Alert type="warning" message={T('You do not have access to this area')} />
      </Wrapper>
    );
  }

  const columns: ColumnsType<TicketSummary> = [
    {
      title: T('Ticket'),
      dataIndex: 'title',
      render: (_: string, t: TicketSummary) => (
        <TicketCell>
          <div className="title">{t.title}</div>
          <div className="meta">
            {t.key} · {t.requestType}
            {t.childTenantName ? ` · ${t.childTenantName}` : ''}
          </div>
        </TicketCell>
      ),
    },
    {
      title: T('Priority'),
      dataIndex: 'priorityCategory',
      render: (_: string, t: TicketSummary) => (
        <Badge color={priorityColor[t.priorityCategory] || FALLBACK_COLOR}>{t.priority || T('Medium priority')}</Badge>
      ),
    },
    {
      title: T('Status'),
      dataIndex: 'statusLabel',
      render: (_: string, t: TicketSummary) => (
        <Badge color={statusColor[t.statusCategory] || FALLBACK_COLOR}>{t.statusLabel}</Badge>
      ),
    },
  ];

  return (
    <Wrapper>
      <Header>
        <h1>{T('Tickets')}</h1>
        <Button type="primary" onClick={() => setModalOpen(true)}>
          {T('Open ticket')}
        </Button>
      </Header>

      <StatsRow>
        <StatCard accent="#8c8c8c">
          <strong>{s.stats.total}</strong>
          <span>{T('Total')}</span>
        </StatCard>
        <StatCard accent={statusColor.open}>
          <strong>{s.stats.open}</strong>
          <span>{T('Open tickets')}</span>
        </StatCard>
        <StatCard accent={statusColor.inProgress}>
          <strong>{s.stats.inProgress}</strong>
          <span>{T('In progress')}</span>
        </StatCard>
        <StatCard accent={statusColor.done}>
          <strong>{s.stats.done}</strong>
          <span>{T('Resolved tickets')}</span>
        </StatCard>
      </StatsRow>
      <small>{T('Based on the loaded list')}</small>

      <Tabs activeKey={s.status} onChange={(k) => s.setStatus(k as 'all' | 'open' | 'closed')}>
        <Tabs.TabPane tab={T('All')} key="all" />
        <Tabs.TabPane tab={T('Open tickets')} key="open" />
        <Tabs.TabPane tab={T('Resolved tickets')} key="closed" />
      </Tabs>

      {s.children.length > 0 && (
        <Select
          allowClear
          style={{ maxWidth: 320 }}
          placeholder={T('Filter by company')}
          value={s.childFilter}
          onChange={(v) => s.setChildFilter(v as number | undefined)}
          options={s.children.map((c) => ({ value: c.tenantPk, label: c.name }))}
        />
      )}

      {s.error && (
        <Alert
          type="error"
          message={
            s.error === 'not-configured'
              ? T('Tickets are not configured for your company yet')
              : T('Could not load tickets')
          }
          action={
            <Button size="small" onClick={s.refresh}>
              {T('Try again')}
            </Button>
          }
        />
      )}

      {!s.loading && !s.error && !s.hasNext && s.tickets.length === 0 ? (
        <Empty description={T('No tickets yet')} />
      ) : (
        <>
          <Table<TicketSummary>
            rowKey="key"
            loading={s.loading}
            dataSource={s.tickets}
            pagination={false}
            onRow={(t) => ({ onClick: () => setSelected(t.key) })}
            columns={columns}
          />
          <Pagination
            currentPage={s.page}
            pageSize={s.pageSize}
            totalCount={s.totalCount}
            onPageChange={(page: number) => s.setPage(page)}
          />
        </>
      )}

      <NewTicketModal visible={modalOpen} onClose={() => setModalOpen(false)} onCreated={s.refresh} />
      <TicketDrawer ticketKey={selected} onClose={() => setSelected(null)} />
    </Wrapper>
  );
};

export default TicketsPage;
